import React from "react";
import PropTypes from "prop-types";
import {
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import {
	ArrowUpDown,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	Loader2,
	Settings2,
	Trash2,
} from "lucide-react";
import Button from "./Button";
import Select from "./Select";

const selectColumn = {
	id: "select",
	header: ({ table }) => (
		<SelectionCheckbox
			ariaLabel="Seleziona tutte le righe visibili"
			checked={table.getIsAllPageRowsSelected()}
			indeterminate={table.getIsSomePageRowsSelected()}
			onChange={(value) => table.toggleAllPageRowsSelected(value)}
		/>
	),
	cell: ({ row }) => (
		<SelectionCheckbox
			ariaLabel="Seleziona riga"
			checked={row.getIsSelected()}
			indeterminate={row.getIsSomeSelected?.()}
			onChange={(value) => row.toggleSelected(value)}
		/>
	),
	enableSorting: false,
	enableHiding: false,
	size: 56,
};

const securityIdColumn = {
	accessorKey: "SecurityID",
	header: ({ column }) => (
		<SortableColumnHeader column={column} title="Security ID" />
	),
	cell: ({ row }) => (
		<span className="text-slate-800">{row.getValue("SecurityID")}</span>
	),
};

const fundNameColumn = {
	accessorKey: "FullName",
	header: ({ column }) => (
		<SortableColumnHeader column={column} title="Fund name" />
	),
	cell: ({ row }) => {
		const isDataReady = row.original.isDataReady;
		return (
			<div className="flex flex-col">
				<span className={`text-slate-800 ${!isDataReady ? 'opacity-50' : ''}`}>
					{row.getValue("FullName") || "N/A"}
				</span>
				{!isDataReady && (
					<span className="text-[10px] font-bold uppercase tracking-tight text-orange-500">
						Security not data-ready or obsolete
					</span>
				)}
			</div>
		);
	},
};

const isinColumn = {
	accessorKey: "Isin",
	header: ({ column }) => (
		<SortableColumnHeader column={column} title="ISIN" />
	),
	cell: ({ row }) => (
		<span className="text-slate-800">{row.getValue("Isin")}</span>
	),
};

const detailsColumn = {
	id: "details",
	enableSorting: false,
	enableHiding: false,
	size: 48,
	cell: ({ row, table }) => {
		const item = row.original;
		return (
			<button
				type="button"
				onClick={() => table.options.meta?.onViewDetails?.(item, row.index)}
				className="inline-flex h-7 w-7 items-center justify-center rounded text-xs text-slate-400 hover:bg-slate-100 hover:text-blue-600"
				title="Visualizza dettaglio security"
			>
				🔍
			</button>
		);
	},
};

const actionsColumn = {
	id: "actions",
	enableSorting: false,
	enableHiding: false,
	cell: ({ row, table }) => (
		<DeleteAction
			id={row.original.SecurityID}
			onRemove={table.options.meta?.onRemoveSecurities}
		/>
	),
};

function getColumns(isAdmin) {
	const cols = [detailsColumn, securityIdColumn, fundNameColumn, isinColumn];
	if (isAdmin) {
		cols.unshift(selectColumn);
		cols.push(actionsColumn);
	}
	return cols;
}

/**
 * Ho rimesso a nuovo questa tabella per darle il look&feel della data-table
 * di shadcn/ui, solo con Tailwind e TanStack Table, così possiamo usarla
 * senza componenti proprietari. Ora, quando isLoading è true, inserisco una
 * riga con spinner dentro la tabella così è chiaro che i dati stanno arrivando.
 * Ho anche attivato esplicitamente la selezione di riga perché le checkbox
 * possano effettivamente cambiare stato e aggiunto un callback per restituire
 * all'App la lista aggiornata delle righe selezionate. Ho spostato la dialog
 * di "Add Securities" fuori da questa tabella così resta atomica e delega
 * all'app la gestione della modale.
 */
function globalFilterFn(row, columnId, filterValue) {
	const val = filterValue.toLowerCase();
	const secId = (row.original.SecurityID || "").toLowerCase();
	const isin = (row.original.Isin || "").toLowerCase();
	const name = (row.original.FullName || "").toLowerCase();
	return secId.includes(val) || isin.includes(val) || name.includes(val);
}

export default function DataTable({
	className,
	data,
	isLoading,
	isAdmin,
	onSelectionChange,
	onEditSecuritiesClick,
	onLookupIdentifiersClick,
	onRemoveSecurities,
	onCreateUniverseClick,
	onDeleteUniverseClick,
	onEditUniverseClick,
	onViewDetails,
}) {
	const [sorting, setSorting] = React.useState([]);
	const [columnVisibility, setColumnVisibility] = React.useState({});
	const [rowSelection, setRowSelection] = React.useState({});
	const [globalFilter, setGlobalFilter] = React.useState("");

	const columns = React.useMemo(() => getColumns(isAdmin), [isAdmin]);

	const table = useReactTable({
		data,
		columns,
		enableRowSelection: isAdmin,
		globalFilterFn,
		state: {
			sorting,
			columnVisibility,
			rowSelection,
			globalFilter,
		},
		meta: {
			onRemoveSecurities,
			onViewDetails,
		},
		onSortingChange: setSorting,
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});

	React.useEffect(() => {
		if (!onSelectionChange) return;

		onSelectionChange(
			table.getSelectedRowModel().rows.map((row) => row.original)
		);
	}, [onSelectionChange, table, rowSelection]);

	const rows = table.getRowModel().rows;
	const headerGroups = table.getHeaderGroups();

	const bodyContent = React.useMemo(() => {
		if (isLoading) {
			return (
				<TableSpinnerRow
					colSpan={columns.length}
					message="Caricamento in corso..."
				/>
			);
		}

		if (rows.length) {
			return rows.map((row) => (
				<tr
					key={row.id}
					className="border-b border-slate-100 last:border-0 hover:bg-slate-50 data-[state=selected]:bg-slate-100/70"
					data-state={row.getIsSelected() ? "selected" : undefined}
				>
					{row.getVisibleCells().map((cell) => (
						<td key={cell.id} className="px-4 py-3 align-middle">
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</td>
					))}
				</tr>
			));
		}

		return (
			<tr>
				<td
					colSpan={columns.length}
					className="px-4 py-10 text-center text-sm font-medium text-slate-500"
				>
					Nessun risultato trovato con questo filtro.
				</td>
			</tr>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, rows, columnVisibility, rowSelection]);

	return (
		<section className={`space-y-4 ${className}`}>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center ">
				<label className="flex w-[220px] max-w-sm items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus-within:ring-2 focus-within:ring-slate-500">
					<span className="text-slate-500">Cerca</span>
					<input
						type="text"
						value={globalFilter}
						placeholder="Nome, Security ID o ISIN..."
						onChange={(event) => setGlobalFilter(event.target.value)}
						className="w-full border-0 bg-transparent text-slate-800 placeholder:text-slate-400 focus:outline-none"
					/>
				</label>
				{isAdmin && (
					<Button
						onClick={onCreateUniverseClick}
						className="bg-blue-500 text-white"
					>
						Create Universe
					</Button>
				)}
				{isAdmin && (
					<Button
						onClick={onDeleteUniverseClick}
						className="bg-red-500 text-white"
					>
						Delete Universe
					</Button>
				)}
				{isAdmin && (
					<Button
						onClick={onEditUniverseClick}
						className="bg-blue-500 text-white"
					>
						Edit Universe
					</Button>
				)}
				{isAdmin && (
					<Button
						onClick={onEditSecuritiesClick}
						className="bg-blue-500 text-white"
					>
						Edit Securities
					</Button>
				)}
				{isAdmin && (
					<Button
						onClick={() => {
							const selectedIds = table
								.getSelectedRowModel()
								.rows.map((row) => row.original.SecurityID);
							if (onRemoveSecurities && selectedIds.length > 0) {
								onRemoveSecurities(selectedIds);
								table.resetRowSelection();
							}
						}}
						className="bg-blue-500 text-white"
					>
						Remove Selected
					</Button>
				)}
				<Button
					onClick={onLookupIdentifiersClick}
					className="bg-blue-500 text-white"
				>
					Look up Identifiers
				</Button>
				<ColumnVisibilityToggle table={table} />
			</div>

			<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
				<div className="w-full overflow-x-auto">
					<table className="w-full caption-bottom text-sm">
						<thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
							{headerGroups.map((headerGroup) => (
								<tr key={headerGroup.id} className="border-b border-slate-200">
									{headerGroup.headers.map((header) => (
										<th key={header.id} className="h-10 px-4 align-middle">
											{header.isPlaceholder
												? null
												: flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody>{bodyContent}</tbody>
					</table>
				</div>
				<PaginationControls table={table} />
			</div>
		</section>
	);
}

DataTable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
	isLoading: PropTypes.bool,
	isAdmin: PropTypes.bool,
	onSelectionChange: PropTypes.func,
	onEditSecuritiesClick: PropTypes.func,
	onLookupIdentifiersClick: PropTypes.func,
	onRemoveSecurities: PropTypes.func,
	onCreateUniverseClick: PropTypes.func,
	onDeleteUniverseClick: PropTypes.func,
	onEditUniverseClick: PropTypes.func,
	onViewDetails: PropTypes.func,
};

/**
 * Ho creato questa riga dedicata per mostrare un piccolo spinner mentre
 * attendiamo i dati: così si capisce che la tabella non è vuota ma in caricamento.
 */
function TableSpinnerRow({ colSpan, message = "Caricamento in corso..." }) {
	return (
		<tr>
			<td
				colSpan={colSpan}
				className="px-4 py-10 text-center text-sm font-medium text-slate-500"
			>
				<div className="inline-flex items-center gap-2 text-slate-500">
					<Loader2 className="h-5 w-5 animate-spin" aria-hidden />
					<span>{message}</span>
				</div>
			</td>
		</tr>
	);
}

TableSpinnerRow.propTypes = {
	colSpan: PropTypes.number.isRequired,
	message: PropTypes.string,
};

/**
 * Ho creato questa checkbox per gestire correttamente lo stato indeterminato
 * che TanStack usa per le selezioni multiple, così l'accessibilità resta a posto.
 */
function SelectionCheckbox({ ariaLabel, checked, indeterminate, onChange }) {
	const ref = React.useRef(null);

	React.useEffect(() => {
		if (ref.current) {
			ref.current.indeterminate = Boolean(indeterminate) && !checked;
		}
	}, [checked, indeterminate]);

	return (
		<input
			ref={ref}
			type="checkbox"
			aria-label={ariaLabel}
			checked={Boolean(checked)}
			onChange={(event) => onChange(event.target.checked)}
			className="h-4 w-4 cursor-pointer rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-500"
		/>
	);
}

SelectionCheckbox.propTypes = {
	ariaLabel: PropTypes.string.isRequired,
	checked: PropTypes.bool,
	indeterminate: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
};

/**
 * Ho preparato questo mini menu per nascondere o mostrare le colonne al volo,
 * in modo simile al tasto View del componente shadcn/ui.
 */
function ColumnVisibilityToggle({ table }) {
	const columns = table
		.getAllLeafColumns()
		.filter((column) => column.getCanHide());

	const [open, setOpen] = React.useState(false);
	const menuRef = React.useRef(null);

	React.useEffect(() => {
		function handleClick(event) {
			if (open && menuRef.current && !menuRef.current.contains(event.target)) {
				setOpen(false);
			}
		}

		function handleEsc(event) {
			if (event.key === "Escape") {
				setOpen(false);
			}
		}

		document.addEventListener("mousedown", handleClick);
		document.addEventListener("keydown", handleEsc);

		return () => {
			document.removeEventListener("mousedown", handleClick);
			document.removeEventListener("keydown", handleEsc);
		};
	}, [open]);

	if (!columns.length) {
		return null;
	}

	return (
		<div className="relative w-full sm:w-auto sm:ml-auto" ref={menuRef}>
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-500 sm:w-auto"
			>
				<Settings2 className="h-4 w-4" aria-hidden />
				<span>Colonne</span>
				<ChevronDown className="h-4 w-4" aria-hidden />
			</button>

			{open ? (
				<div className="absolute right-0 z-20 mt-2 w-56 rounded-md border border-slate-200 bg-white p-3 text-sm shadow-lg">
					<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
						Toggle columns
					</p>
					<div className="space-y-1">
						{columns.map((column) => (
							<label
								key={column.id}
								className="flex items-center gap-2 rounded-md px-2 py-1 text-slate-800 hover:bg-slate-50"
							>
								<input
									type="checkbox"
									checked={column.getIsVisible()}
									onChange={(event) =>
										column.toggleVisibility(event.target.checked)
									}
									className="h-4 w-4 cursor-pointer rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-500"
								/>
								<span className="capitalize">{column.id}</span>
							</label>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
}

ColumnVisibilityToggle.propTypes = {
	table: PropTypes.object.isRequired,
};

/**
 * Controlli di paginazione ispirati al footer della data-table shadcn: ora uso
 * anche il Select custom per restare coerente senza portarmi dietro Radix.
 */
function PaginationControls({ table }) {
	return (
		<div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
			<div className="text-sm text-slate-600">
				{table.getFilteredSelectedRowModel().rows.length} di{" "}
				{table.getFilteredRowModel().rows.length} riga(e) selezionate.
			</div>
			<div className="flex flex-wrap items-center gap-3">
				<div className="flex items-center gap-2 text-sm text-slate-700">
					<span>Righe per pagina</span>
					<Select
						ariaLabel="Seleziona quante righe mostrare"
						value={String(table.getState().pagination.pageSize)}
						onValueChange={(value) => table.setPageSize(Number(value))}
						options={[5, 10, 20, 30, 40, 50].map((size) => ({
							value: String(size),
							label: size,
						}))}
						className="w-[140px]"
					/>
				</div>

				<div className="flex items-center gap-2 text-sm font-medium text-slate-800">
					<button
						type="button"
						className="hidden h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:flex"
						onClick={() => table.setPageIndex(0)}
						disabled={!table.getCanPreviousPage()}
					>
						<ChevronsLeft className="h-4 w-4" aria-hidden />
						<span className="sr-only">Prima pagina</span>
					</button>
					<button
						type="button"
						className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
						onClick={() => table.previousPage()}
						disabled={!table.getCanPreviousPage()}
					>
						<ChevronLeft className="h-4 w-4" aria-hidden />
						<span className="sr-only">Pagina precedente</span>
					</button>
					<span className="px-1 text-xs text-slate-600 sm:text-sm">
						Pagina {table.getState().pagination.pageIndex + 1} di{" "}
						{table.getPageCount()}
					</span>
					<button
						type="button"
						className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
						onClick={() => table.nextPage()}
						disabled={!table.getCanNextPage()}
					>
						<ChevronRight className="h-4 w-4" aria-hidden />
						<span className="sr-only">Pagina successiva</span>
					</button>
					<button
						type="button"
						className="hidden h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:flex"
						onClick={() => table.setPageIndex(table.getPageCount() - 1)}
						disabled={!table.getCanNextPage()}
					>
						<ChevronsRight className="h-4 w-4" aria-hidden />
						<span className="sr-only">Ultima pagina</span>
					</button>
				</div>
			</div>
		</div>
	);
}

PaginationControls.propTypes = {
	table: PropTypes.object.isRequired,
};

/**
 * Questo badge di stato l'ho pensato per leggere al volo l'esito di un pagamento.
 */
function StatusBadge({ status }) {
	const palette = {
		success: "border-emerald-200 bg-emerald-100 text-emerald-800",
		processing: "border-amber-200 bg-amber-100 text-amber-800",
		failed: "border-rose-200 bg-rose-100 text-rose-800",
		pending: "border-slate-200 bg-slate-100 text-slate-800",
	};

	const tone = palette[status] ?? palette.pending;

	return (
		<span
			className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}
		>
			<span className="h-2 w-2 rounded-full bg-current/70" aria-hidden />
			<span className="capitalize">{status}</span>
		</span>
	);
}

StatusBadge.propTypes = {
	status: PropTypes.string,
};

/**
 * Ho messo questo pulsante rosso con l'icona del cestino solo per
 * rappresentare graficamente l'azione di delete: la logica vera la colleghiamo dopo.
 */
function DeleteAction({ id, onRemove }) {
	return (
		<button
			type="button"
			title="Elimina"
			aria-label={`Elimina riga ${id}`}
			onClick={(event) => {
				event.preventDefault();
				if (onRemove) {
					onRemove([id]);
				}
			}}
			className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-600 shadow-sm transition hover:bg-rose-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500"
		>
			<Trash2 className="h-4 w-4" aria-hidden />
		</button>
	);
}

DeleteAction.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onRemove: PropTypes.func,
};

function SortableColumnHeader({ column, title }) {
	const sorted = column.getIsSorted();

	return (
		<button
			type="button"
			onClick={() => column.toggleSorting(sorted === "asc")}
			className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-500"
		>
			<span className="leading-none">{title}</span>
			<span className="sr-only">
				Ordina {sorted === "asc" ? "decrescente" : "crescente"}
			</span>
			<ArrowUpDown className="h-4 w-4 text-slate-500" aria-hidden />
		</button>
	);
}

SortableColumnHeader.propTypes = {
	column: PropTypes.object.isRequired,
	title: PropTypes.string.isRequired,
};